import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TablePagination, TableSortLabel, TextField, Tooltip } from '@mui/material'
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import Swal from 'sweetalert2'
import { useNavigate, useLocation } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import AddButton from '../../components/AddButton'
import Loading from '../../components/Loading'
import { isValid, handleAxiosError, handleNumber, formatQuantity } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'
import forecastCalculationBasisAPI from '../../services/api/Forecasts/forecastCalculationBasisAPI'

const emptyRow = () => ({ itemId: '', itemLabel: '', manualOverrideQty: '', calculatedForecastQty: null, firmOrderQty: null })

const CreateForecast = () => {

    const { t } = useTranslation()
    const { company_id, config, choosesite, setCompany_id, setChoosesite } = useStateContext()
    const navigate = useNavigate()
    const location = useLocation()

    /* state */
    const [isLoading, setIsLoading] = useState(false)
    const [customers, setCustomers] = useState([])
    const [sites, setSites] = useState([])
    const [items, setItems] = useState([])
    const [calculationBasisOptions, setCalculationBasisOptions] = useState([])
    const [rowId, setRowId] = useState(0)
    const [order, setOrder] = useState('asc')
    const [orderBy, setOrderBy] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    /* form */
    const [customerId, setCustomerId] = useState('')
    const [customerName, setCustomerName] = useState('')
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [calculationBasis, setCalculationBasis] = useState('')
    const [periodFrom, setPeriodFrom] = useState(dayjs())
    const [periodTo, setPeriodTo] = useState(dayjs())
    const [forecastItems, setForecastItems] = useState([])

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/forecasts')
    }

    const hasGeneralInfo = (
        isValid(customerId)
        && isValid(siteId)
        && isValid(calculationBasis)
        && isValid(periodFrom)
        && isValid(periodTo)
    )

    const isFormValid = () => (
        hasGeneralInfo
        && forecastItems.some(row => isValid(row.itemId))
    )

    useEffect(() => {
        function handleKeyDown(e) {
            if (e.keyCode == '27') {
                goBack()
            }
        }

        document.addEventListener('keydown', handleKeyDown);

        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
        }
    }, [location])

    useEffect(() => {
        if (!isValid(company_id)) {
            setCompany_id(localStorage.getItem('company_id'))
        }
        if (!isValid(choosesite)) {
            setChoosesite(localStorage.getItem('site'))
        }
    }, [])

    useEffect(() => {
        if (isValid(company_id)) {
            getCustomers(company_id)
            getSites(company_id)
        }
    }, [company_id])

    useEffect(() => {
        if (isValid(siteId) && isValid(customerId) && isValid(calculationBasis) && isValid(periodFrom) && isValid(periodTo)) {
            getItems(siteId, customerId, calculationBasis, periodFrom, periodTo)
        } else {
            setItems([])
        }
    }, [siteId, customerId, calculationBasis, periodFrom, periodTo])

    useEffect(() => {
        if (!isValid(siteId) && sites.length > 0) {
            const defaultSiteId = choosesite || localStorage.getItem('site')
            const defaultSite = sites.find(site => String(site.id) === String(defaultSiteId))

            if (defaultSite) {
                setSiteId(defaultSite.id)
                setSiteName(defaultSite.name)
            }
        }
    }, [sites, choosesite])

    useEffect(() => {
        getCalculationBasisOptions()
    }, [])

    /* requests */

    const getCalculationBasisOptions = async () => {
        await forecastCalculationBasisAPI.getForecastCalculationBasis(config)
            .then((data) => {
                setCalculationBasisOptions(data)
            })
    }

    const getCustomers = async (id) => {
        await axios
            .get(`/api/customers?company_id=${id}&blocked=0`, config)
            .then(res => {
                setCustomers(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getSites = async (id) => {
        await axios
            .get(`/api/sites?company_id=${id}`, config)
            .then(res => {
                setSites(res.data)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getItems = async (site, customer, basis, from, to) => {
        const periodFromParam = dayjs(from).format('YYYY-MM-DD')
        const periodToParam = dayjs(to).format('YYYY-MM-DD')

        await axios
            .get(`/api/forecast-items?site_id=${site}&customer_id=${customer}&calculation_basis=${basis}&period_from=${periodFromParam}&period_to=${periodToParam}`, config)
            .then(res => {
                setItems(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const createForecast = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        const formData = {}

        formData['customer_id'] = customerId
        formData['site_id'] = Number(siteId)
        formData['period_from'] = dayjs(periodFrom).format('YYYY-MM-DD')
        formData['period_to'] = dayjs(periodTo).format('YYYY-MM-DD')
        formData['calculation_basis'] = calculationBasis
        formData['forecast_items'] = forecastItems
            .filter(row => isValid(row.itemId))
            .map(row => ({
                item_id: row.itemId,
                calculated_forecast_qty: rowCalculatedForecastQty(row),
                manual_override_qty: row.manualOverrideQty !== '' ? Number(row.manualOverrideQty) : null,
                final_forecast_qty: rowFinalForecastQty(row),
                firm_order_qty: rowFirmOrderQty(row),
                remaining_forecast_qty: rowRemainingForecastQty(row)
            }))

        await axios.post(`/api/forecasts`, formData, config).then(({ data }) => {
            Swal.fire({
                icon: "success",
                customClass: 'success',
                showCloseButton: true,
                iconColor: '#00B78E',
                text: data?.success?.message
            })

            const createdId = data?.success?.created?.id
            if (createdId) {
                navigate(`/forecasts/${createdId}`, { state: { prevPathname: location.state?.prevPathname ?? '/forecasts' } })
            } else {
                goBack()
            }
        }).catch(({ response }) => {
            handleAxiosError({ response: response, showDescription: true })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    /* handlers */

    const handleCustomerChange = (a, b) => {
        setCustomerId(b?.id || '')
        setCustomerName(b?.label || '')
        setForecastItems([])
    }

    const handleSiteChange = (a, b) => {
        setSiteId(b?.id || '')
        setSiteName(b?.label || '')
        setForecastItems([])
    }

    const addRow = () => {
        setForecastItems(prev => [...prev, { ...emptyRow(), id: rowId }])
        setRowId(prev => prev + 1)
    }

    const removeRow = (id) => {
        setForecastItems(prev => prev.filter(row => row.id !== id))
    }

    const clearAllRows = () => {
        setForecastItems([])
    }

    const handleRowItemChange = (id, a, b) => {
        const selectedItem = items.find(item => item.item_id === b?.id)

        setForecastItems(prev => prev.map(row => row.id === id ? {
            ...row,
            itemId: b?.id || '',
            itemLabel: b?.label || '',
            calculatedForecastQty: selectedItem ? selectedItem.calculated_forecast_qty : null,
            firmOrderQty: selectedItem ? selectedItem.firm_order_qty : null,
        } : row))
    }

    const handleRowManualOverrideChange = (id, event) => {
        handleNumber({
            event,
            setter: (value) => setForecastItems(prev => prev.map(row => row.id === id ? { ...row, manualOverrideQty: value } : row))
        })
    }

    const optionsCustomers = customers?.map(option => ({ label: option.customer_no + ' - ' + option.name, id: option.id }))
    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))
    const optionsItems = items?.map(option => ({ label: option.item_no + ' - ' + option.description, id: option.item_id }))
    const allItemsSelected = optionsItems.length > 0 && forecastItems.filter(row => isValid(row.itemId)).length >= optionsItems.length

    const rowCalculatedForecastQty = (row) => isValid(row.calculatedForecastQty) ? row.calculatedForecastQty : 0

    const rowFirmOrderQty = (row) => isValid(row.firmOrderQty) ? row.firmOrderQty : 0

    const rowFinalForecastQty = (row) => {
        if (row.manualOverrideQty !== '') {
            return Number(row.manualOverrideQty)
        }

        return rowCalculatedForecastQty(row)
    }

    const rowRemainingForecastQty = (row) => {
        return Math.max(0, rowFinalForecastQty(row) - rowFirmOrderQty(row))
    }

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const getItemSortValue = (row, property) => {
        switch (property) {
            case 'item': return row.itemLabel ?? ''
            case 'calculatedForecastQty': return rowCalculatedForecastQty(row)
            case 'manualOverrideQty': return row.manualOverrideQty !== '' ? Number(row.manualOverrideQty) : null
            case 'finalForecastQty': return rowFinalForecastQty(row)
            case 'firmOrderQty': return rowFirmOrderQty(row)
            case 'remainingForecastQty': return rowRemainingForecastQty(row)
            default: return ''
        }
    }

    const compareItemSortValues = (a, b) => {
        if (!isValid(a)) {
            return isValid(b) ? -1 : 0
        }
        if (!isValid(b)) {
            return 1
        }
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b
        }
        return String(a).localeCompare(String(b))
    }

    const sortedForecastItems = useMemo(() => {
        if (!orderBy) {
            return forecastItems
        }
        const sorted = [...forecastItems].sort((a, b) => compareItemSortValues(getItemSortValue(a, orderBy), getItemSortValue(b, orderBy)))
        return order === 'desc' ? sorted.reverse() : sorted
    }, [forecastItems, order, orderBy])

    const paginatedForecastItems = useMemo(() => (
        sortedForecastItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    ), [sortedForecastItems, page, rowsPerPage])

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedForecastItems.length / rowsPerPage) - 1)
        if (page > maxPage) {
            setPage(maxPage)
        }
    }, [sortedForecastItems, rowsPerPage])

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const totalCalculatedForecastQty = forecastItems.reduce((total, row) => total + rowCalculatedForecastQty(row), 0)

    const totalManualOverrideQty = forecastItems.reduce((total, row) => {
        return row.manualOverrideQty !== '' ? (total ?? 0) + Number(row.manualOverrideQty) : total
    }, null)

    const totalFinalForecastQty = forecastItems.reduce((total, row) => total + rowFinalForecastQty(row), 0)

    const totalFirmOrderQty = forecastItems.reduce((total, row) => total + rowFirmOrderQty(row), 0)

    const totalRemainingForecastQty = forecastItems.reduce((total, row) => total + rowRemainingForecastQty(row), 0)

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#336195] text-[35px] mr-4'>•</span> {t('create_new_forecast')}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-x-5'>
                                {/* Customer */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('customer')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        id="forecast-customer-autocomplete"
                                        options={optionsCustomers}
                                        value={customerId ? { label: customerName, id: customerId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        onChange={handleCustomerChange}
                                        renderInput={(params) => <TextField {...params} autoFocus={true} />}
                                    />
                                </div>

                                {/* Period From */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('period_from')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={periodFrom}
                                            onChange={(newValue) => {
                                                setPeriodFrom(newValue)
                                                if (newValue && periodTo && dayjs(periodTo).isBefore(dayjs(newValue), 'day')) {
                                                    setPeriodTo(null)
                                                }
                                                setForecastItems([])
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Period To */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('period_to')} *</p>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            sx={TEXTFIELD_STYLE}
                                            format="YYYY-MM-DD"
                                            value={periodTo}
                                            minDate={periodFrom || undefined}
                                            onChange={(newValue) => { setPeriodTo(newValue); setForecastItems([]) }}
                                        />
                                    </LocalizationProvider>
                                </div>

                                {/* Site */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('site')} *</p>
                                    <Autocomplete
                                        disablePortal
                                        id="forecast-site-autocomplete"
                                        options={optionsSites}
                                        value={siteId ? { label: siteName, id: siteId } : null}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{
                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                        }}
                                        onChange={handleSiteChange}
                                        renderInput={(params) => <TextField {...params} />}
                                    />
                                </div>

                                {/* Calculation Basis */}
                                <div className='w-full pb-4'>
                                    <p className="text-[14px] text-[#718096]">{t('calculation_basis')} *</p>
                                    <FormControl sx={{ width: '100%' }}>
                                        <Select
                                            displayEmpty
                                            value={calculationBasis}
                                            onChange={(e) => { setCalculationBasis(e.target.value); setForecastItems([]) }}
                                            sx={TEXTFIELD_STYLE}
                                        >
                                            <MenuItem value="">{t('select_calculation_basis')}</MenuItem>
                                            {calculationBasisOptions.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                        </div>

                        <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                            <div className='pt-1 pl-3'>
                                <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('forecasts_list')}</p>
                            </div>
                            <div className='flex justify-end p-5 pt-0 border-b'>
                                <Tooltip disableInteractive title={!hasGeneralInfo ? t('complete_general_information_first') : allItemsSelected ? t('all_items_added') : t('addItem')} placement='bottom'>
                                    <div>
                                        <AddButton disabled={!hasGeneralInfo || allItemsSelected} onClick={addRow}><i className="fa-solid fa-plus text-[#333333]"></i></AddButton>
                                    </div>
                                </Tooltip>
                            </div>

                            {forecastItems.length > 0 ? (
                                <div className='flex justify-end w-full p-2'>
                                    <button type="button" className='px-2 py-1 text-white bg-red-600 font-bold rounded-md mb-1 roboto text-xs uppercase' onClick={clearAllRows}>{t('clear_all')}</button>
                                </div>
                            ) : null}

                            <div className='overflow-x-auto p-5 pt-0' style={!hasGeneralInfo ? { opacity: 0.4 } : undefined}>
                                <table className='w-full text-left'>
                                    <thead>
                                        <tr className='text-[#88909C] text-[13px] border-b'>
                                            <th className='py-2 pr-2 font-[400]'>
                                                <TableSortLabel active={orderBy === 'item'} direction={orderBy === 'item' ? order : 'asc'} onClick={() => handleRequestSort('item')}>{t('item')} *</TableSortLabel>
                                            </th>
                                            <th className='py-2 px-2 font-[400] text-center'>
                                                <TableSortLabel active={orderBy === 'calculatedForecastQty'} direction={orderBy === 'calculatedForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('calculatedForecastQty')}>{t('calculated_forecast_qty')}</TableSortLabel>
                                            </th>
                                            <th className='py-2 px-2 font-[400] text-center'>
                                                <TableSortLabel active={orderBy === 'manualOverrideQty'} direction={orderBy === 'manualOverrideQty' ? order : 'asc'} onClick={() => handleRequestSort('manualOverrideQty')}>{t('manual_override_qty')}</TableSortLabel>
                                            </th>
                                            <th className='py-2 px-2 font-[400] text-center'>
                                                <TableSortLabel active={orderBy === 'finalForecastQty'} direction={orderBy === 'finalForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('finalForecastQty')}>{t('final_forecast_qty')}</TableSortLabel>
                                            </th>
                                            <th className='py-2 px-2 font-[400] text-center'>
                                                <TableSortLabel active={orderBy === 'firmOrderQty'} direction={orderBy === 'firmOrderQty' ? order : 'asc'} onClick={() => handleRequestSort('firmOrderQty')}>{t('firm_order_qty')}</TableSortLabel>
                                            </th>
                                            <th className='py-2 px-2 font-[400] text-center'>
                                                <TableSortLabel active={orderBy === 'remainingForecastQty'} direction={orderBy === 'remainingForecastQty' ? order : 'asc'} onClick={() => handleRequestSort('remainingForecastQty')}>{t('remaining_forecast_qty')}</TableSortLabel>
                                            </th>
                                            <th className='py-2 pl-2 font-[400] text-center'>{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedForecastItems.length <= 0 ? (
                                            <tr>
                                                <td colSpan={7} className='text-center py-4 text-[#88909C]'>{!hasGeneralInfo ? t('complete_general_information_first') : t('no_data')}</td>
                                            </tr>
                                        ) : null}
                                        {paginatedForecastItems.map((row) => (
                                            <tr key={row.id} className='border-b'>
                                                <td className='py-2 pr-2' style={{ minWidth: '220px' }}>
                                                    <Autocomplete
                                                        disablePortal
                                                        size="small"
                                                        id={`forecast-item-autocomplete-${row.id}`}
                                                        options={optionsItems.filter(option => !forecastItems.some(r => r.id !== row.id && r.itemId === option.id))}
                                                        value={row.itemId ? { label: row.itemLabel, id: row.itemId } : null}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        sx={{
                                                            width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                                            boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                                        }}
                                                        onChange={(a, b) => handleRowItemChange(row.id, a, b)}
                                                        renderInput={(params) => <TextField {...params} placeholder={t('select_item')} />}
                                                    />
                                                </td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(rowCalculatedForecastQty(row))}</td>
                                                <td className='py-2 px-2 text-center' style={{ minWidth: '110px' }}>
                                                    <TextField
                                                        size="small"
                                                        placeholder={t('enter_qty')}
                                                        value={row.manualOverrideQty}
                                                        onChange={(e) => handleRowManualOverrideChange(row.id, e)}
                                                        sx={{ width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' } }}
                                                        inputProps={{ style: { textAlign: 'center' } }}
                                                    />
                                                </td>
                                                <td className='py-2 px-2 text-center font-[700]'>{formatQuantity(rowFinalForecastQty(row))}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(rowFirmOrderQty(row))}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(rowRemainingForecastQty(row))}</td>
                                                <td className='py-2 pl-2 text-center'>
                                                    <Tooltip disableInteractive title={t('delete')} placement='bottom'>
                                                        <div style={{ color: 'rgba(0,0,0,.54)' }}>
                                                            <span style={{ cursor: 'pointer' }} className="flex justify-center items-center hover:rounded-full icons p-2 hover:bg-zinc-200" onClick={() => removeRow(row.id)}><i className="fa-solid fa-trash"></i></span>
                                                        </div>
                                                    </Tooltip>
                                                </td>
                                            </tr>
                                        ))}
                                        {forecastItems.length > 0 ? (
                                            <tr className='font-[700]'>
                                                <td className='py-2 pr-2'>{t('customer_total')}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(totalCalculatedForecastQty)}</td>
                                                <td className='py-2 px-2 text-center'>{isValid(totalManualOverrideQty) ? formatQuantity(totalManualOverrideQty) : '-'}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(totalFinalForecastQty)}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(totalFirmOrderQty)}</td>
                                                <td className='py-2 px-2 text-center'>{formatQuantity(totalRemainingForecastQty)}</td>
                                                <td className='py-2 pl-2 text-center'></td>
                                            </tr>
                                        ) : null}
                                    </tbody>
                                </table>
                            </div>

                            {sortedForecastItems.length > 0 ? (
                                <TablePagination
                                    className='table-pagination'
                                    labelRowsPerPage={t('rowsPerPage')}
                                    rowsPerPageOptions={[10, 15]}
                                    component="div"
                                    count={sortedForecastItems.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            ) : null}
                        </div>

                        <div className='w-full flex justify-end gap-3'>
                            <button type="button"  onClick={createForecast} className='text-white font-[700] rounded-md py-2.5 px-5 uppercase text-[14px] bg-[#DBA34B] hover:bg-[#CF7300] disabled:bg-[#B8B7BC]'>
                                <span className='pr-2'><i className="fa-solid fa-plus"></i></span> {t('create')}
                            </button>
                        </div>
                    </div>
            </Box>
        </>
    )
}



export default CreateForecast
